import { ICellRendererParams } from 'ag-grid-enterprise';
import Chip, { ChipProps } from '@mui/material/Chip';
import React from 'react';
import { Transaction, TransactionStatus, TransactionType } from '../../graphql/types';
import { CurrencyText } from '../input-formatters/CurrencyText';
import StatusChip from '../StatusChip';

type ColorMapType = Record<TransactionType, Partial<Record<TransactionStatus, string>>>;
export const transactionChipColor = (data: Transaction): string => {
  const colorMap: ColorMapType = {
    [TransactionType.Debit]: {
      [TransactionStatus.Processed]: 'info',
      [TransactionStatus.Pending]: 'info',
      [TransactionStatus.Cleared]: 'success',
      [TransactionStatus.Returned]: 'error',
      [TransactionStatus.Nsf]: 'error',
      [TransactionStatus.Cancelled]: 'warning'
    },
    [TransactionType.AchProgramFee]: {
      [TransactionStatus.Processed]: 'info',
      [TransactionStatus.Pending]: 'info',
      [TransactionStatus.Cleared]: 'feeClear',
      [TransactionStatus.Returned]: 'error',
      [TransactionStatus.Nsf]: 'error',
      [TransactionStatus.Cancelled]: 'warning'
    },
    [TransactionType.DefaultFee]: {
      [TransactionStatus.Processed]: 'info',
      [TransactionStatus.Pending]: 'info',
      [TransactionStatus.Cleared]: 'feeClear',
      [TransactionStatus.Returned]: 'error',
      [TransactionStatus.Nsf]: 'error',
      [TransactionStatus.Cancelled]: 'warning'
    },
    [TransactionType.NsfFee]: {
      [TransactionStatus.Processed]: 'info',
      [TransactionStatus.Pending]: 'info',
      [TransactionStatus.Cleared]: 'feeClear',
      [TransactionStatus.Returned]: 'error',
      [TransactionStatus.Nsf]: 'error',
      [TransactionStatus.Cancelled]: 'warning'
    },
    [TransactionType.Collection]: {
      [TransactionStatus.Cleared]: 'success',
      [TransactionStatus.Cancelled]: 'warning'
    },
    [TransactionType.Syndication]: {
      [TransactionStatus.Cleared]: 'warning',
      [TransactionStatus.Cancelled]: 'warning'
    },
    [TransactionType.FundingWallet]: {
      [TransactionStatus.Cleared]: 'info',
      [TransactionStatus.Cancelled]: 'warning'
    },
    [TransactionType.Remittance]: {
      [TransactionStatus.Cleared]: 'warning',
      [TransactionStatus.Cancelled]: 'warning'
    },
    [TransactionType.RemittanceFee]: {
      [TransactionStatus.Cleared]: 'warning',
      [TransactionStatus.Cancelled]: 'warning'
    },
    [TransactionType.Refund]: {
      [TransactionStatus.Cleared]: 'success',
      [TransactionStatus.Cancelled]: 'warning'
    },
    [TransactionType.Expense]: {
      [TransactionStatus.Cleared]: 'success',
      [TransactionStatus.Cancelled]: 'warning'
    },
    [TransactionType.CrmAccessFee]: {
      [TransactionStatus.Cleared]: 'success',
      [TransactionStatus.Cancelled]: 'warning'
    },
    [TransactionType.OtherFee]: {
      [TransactionStatus.Cleared]: 'success',
      [TransactionStatus.Cancelled]: 'warning'
    },
    [TransactionType.ServiceFee]: {
      [TransactionStatus.Cleared]: 'success',
      [TransactionStatus.Cancelled]: 'warning'
    },
    [TransactionType.Credit]: {
      [TransactionStatus.Cleared]: 'success',
      [TransactionStatus.Cancelled]: 'warning'
    }
  };
  return colorMap[data.type]?.[data.status] || 'default';
};

export const statusLabel = (data: Transaction): string => {
  switch (data.status) {
    case TransactionStatus.Cleared:
      return 'CLEARED';
    case TransactionStatus.Returned:
      if (!data.returnCode) {
        return TransactionStatus.Returned;
      }
      switch (data.returnCode) {
        case 'R08':
          return 'STOP PMT R08';
        case 'R29':
          return 'STOP PMT R29';
        case 'R02':
          return 'ACC CLOSED R02';
        case 'R03':
          return 'WRONG ACC R03';
        case 'R04':
          return 'WRONG ACC R04';
        case 'R10':
          return 'UNAUTH DEBIT R10';
        case 'R16':
          return 'ACC FROZEN R16';
        case 'R20':
          return 'NON-TRAN ACC R20';
        default:
          return 'RETURNED';
      }
    default:
      return data.status.split('_').join(' ');
  }
};
const customColorStyles: Record<string, { backgroundColor: string; color: string }> = {
  feeClear: { backgroundColor: '#342C1A', color: '#F8E6A0' }
};
const typeLabel = (data: Transaction): string => {
  return data.type.split('_').join(' ');
};

export const getChipStyle = (color: string) => {
  return customColorStyles[color] || {};
};

export const isCustomColor = (color: string) => {
  return !!customColorStyles[color];
};

export const TransactionStatusCell = ({
  data,
  node,
  useCompactViewStyle = false
}: ICellRendererParams & {
  useCompactViewStyle?: boolean;
}) => {
  if (node.isRowPinned()) {
    return null;
  }
  const color = transactionChipColor(data);
  const style = getChipStyle(color);
  const label = statusLabel(data);
  return useCompactViewStyle ? (
    <StatusChip label={label} color={(!isCustomColor(color) ? color : undefined) as ChipProps['color']} size="small" variant="light" style={style} />
  ) : (
    <Chip label={label} color={(!isCustomColor(color) ? color : undefined) as ChipProps['color']} size="small" variant="light" style={style} />
  );
};
export const TransactionDefaultStatusCell = ({ data, node }: ICellRendererParams) => {
  if (node.isRowPinned()) {
    return null;
  }
  return <Chip label={statusLabel(data)} color={'default'} size="small" variant="light" />;
};
export const TransactionDefaultTypeCell = ({
  data,
  node,
  ledgerTransaction = false,
  useCompactViewStyle = false
}: ICellRendererParams & {
  ledgerTransaction?: boolean;
  useCompactViewStyle?: boolean;
}) => {
  if (node.isRowPinned()) {
    return null;
  }

  const label = ledgerTransaction
    ? data.type === TransactionType.Debit || data.type === TransactionType.Collection
      ? 'CREDIT'
      : data.type === TransactionType.Credit
        ? 'DEBIT'
        : typeLabel(data)
    : typeLabel(data);

  return useCompactViewStyle ? (
    <StatusChip label={label} color={'default'} size="small" variant="light" />
  ) : (
    <Chip label={label} color={'default'} size="small" variant="light" />
  );
};
export const TransactionTypeCell = ({ data, node }: ICellRendererParams) => {
  if (node.isRowPinned()) {
    return null;
  }
  const color = transactionChipColor(data);
  const style = getChipStyle(color);
  return <Chip label={typeLabel(data)} color={(!isCustomColor(color) ? color : undefined) as ChipProps['color']} size="small" variant="light" style={style} />;
};

export const TransactionAmountCell = ({
  value,
  data,
  node,
  useCompactViewStyle = false
}: ICellRendererParams & {
  useCompactViewStyle?: boolean;
}) => {
  if (node.isRowPinned()) {
    return <CurrencyText value={value} />;
  }
  let color = transactionChipColor(data);
  if (
    data.status === TransactionStatus.Cleared &&
    [TransactionType.Collection, TransactionType.Refund, TransactionType.Expense, TransactionType.OtherFee].includes(data.type)
  ) {
    color = data.amount >= 0 ? 'success' : 'error';
  }
  const style = getChipStyle(color);
  return useCompactViewStyle ? (
    <StatusChip
      color={(!isCustomColor(color) ? color : 'default') as ChipProps['color']}
      label={<CurrencyText value={value} />}
      size="small"
      variant="light"
      style={style}
    />
  ) : (
    <Chip
      color={(!isCustomColor(color) ? color : 'default') as ChipProps['color']}
      label={<CurrencyText value={value} />}
      size="small"
      variant="light"
      style={style}
    />
  );
};
